'use client';

import { useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function usePeopleAndCultureAnimations() {
  const pageRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const commitRef = useRef<HTMLDivElement>(null);
  const lifeRef = useRef<HTMLDivElement>(null);
  const testRef = useRef<HTMLDivElement>(null);
  const initRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add('(min-width: 1024px)', () => {
        gsap.from('[data-hero-text]', {
          y: 60,
          opacity: 0,
          duration: 1.2,
          stagger: 0.18,
          ease: 'power3.out',
          delay: 0.3,
        });

        gsap.to('.hero-img-wrap', {
          scrollTrigger: {
            trigger: heroRef.current,
            start: 'top top',
            end: 'bottom top',
            scrub: 1.5,
          },
          y: '22%',
          ease: 'none',
        });

        el.querySelectorAll('[data-dash]').forEach((dash) => {
          gsap.from(dash, {
            scrollTrigger: {
              trigger: dash,
              start: 'top 92%',
              toggleActions: 'play none none none',
            },
            scaleX: 0,
            transformOrigin: 'left center',
            duration: 0.55,
            ease: 'power2.out',
          });
        });

        el.querySelectorAll('[data-reveal]').forEach((node) => {
          gsap.from(node, {
            scrollTrigger: {
              trigger: node,
              start: 'top 88%',
              toggleActions: 'play none none none',
            },
            y: 40,
            opacity: 0,
            duration: 0.9,
            ease: 'power3.out',
          });
        });

        gsap.from('[data-commit-card]', {
          scrollTrigger: {
            trigger: commitRef.current,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
          y: 70,
          opacity: 0,
          duration: 0.75,
          stagger: 0.13,
          ease: 'power3.out',
        });

        gsap.from('[data-init-left]', {
          scrollTrigger: {
            trigger: initRef.current,
            start: 'top 82%',
            toggleActions: 'play none none none',
          },
          x: -80,
          opacity: 0,
          duration: 1.1,
          ease: 'power3.out',
        });

        gsap.from('[data-init-right]', {
          scrollTrigger: {
            trigger: initRef.current,
            start: 'top 82%',
            toggleActions: 'play none none none',
          },
          x: 80,
          opacity: 0,
          duration: 1.1,
          ease: 'power3.out',
        });

        el.querySelectorAll('[data-stat-box]').forEach((box, i) => {
          gsap.from(box, {
            scrollTrigger: {
              trigger: box,
              start: 'top 88%',
              toggleActions: 'play none none none',
            },
            scale: 0.8,
            opacity: 0,
            duration: 0.45,
            delay: i * 0.09,
            ease: 'back.out(1.7)',
          });
        });

        el.querySelectorAll('[data-life-cell]').forEach((cell, i) => {
          gsap.from(cell, {
            scrollTrigger: {
              trigger: lifeRef.current,
              start: 'top 82%',
              toggleActions: 'play none none none',
            },
            scale: 0.88,
            opacity: 0,
            duration: 0.85,
            delay: i * 0.09,
            ease: 'power3.out',
          });
        });

        el.querySelectorAll('[data-test-panel]').forEach((panel, i) => {
          gsap.from(panel, {
            scrollTrigger: {
              trigger: testRef.current,
              start: 'top 82%',
              toggleActions: 'play none none none',
            },
            y: 35,
            opacity: 0,
            duration: 0.9,
            delay: i * 0.18,
            ease: 'power3.out',
          });
        });

        gsap.from('[data-cta-left]', {
          scrollTrigger: {
            trigger: '[data-cta-left]',
            start: 'top 86%',
            toggleActions: 'play none none none',
          },
          x: -50,
          opacity: 0,
          duration: 1,
          ease: 'power3.out',
        });

        gsap.from('[data-cta-right]', {
          scrollTrigger: {
            trigger: '[data-cta-right]',
            start: 'top 86%',
            toggleActions: 'play none none none',
          },
          x: 50,
          opacity: 0,
          duration: 1,
          ease: 'power3.out',
        });
      });
    }, el);

    const t = setTimeout(() => ScrollTrigger.refresh(), 400);

    return () => {
      clearTimeout(t);
      ctx.revert();
    };
  }, []);

  return { pageRef, heroRef, commitRef, lifeRef, testRef, initRef };
}
